import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Bell, BellOff, Smartphone, CheckCircle2, ShieldCheck } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useFetch } from "../../lib/useFetch";
import { canInstall, isStandalone, isIos, promptInstall, onPwaChange, notifySupported, notifyEnabled,
  enableNotifications, disableNotifications } from "../../lib/pwa";

const Row = ({ icon: Icon, title, text, children, testid }) => (
  <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-slate-50 p-5" data-testid={testid}>
    <div className="flex items-start gap-3"><Icon size={20} className="mt-0.5 shrink-0 text-[#00A8B5]" />
      <div><p className="font-semibold text-[#0A2540]">{title}</p><p className="text-sm text-slate-500">{text}</p></div></div>
    {children}
  </div>
);

const TrustedRow = () => {
  const { t } = useLang();
  const { data, reload } = useFetch("/auth/trusted-devices");
  const { user } = useAuth();
  const forget = async () => {
    try {
      await api.delete("/auth/trusted-devices");
      const map = JSON.parse(localStorage.getItem("aa_trusted") || "{}");
      delete map[(user?.email || "").toLowerCase()];
      localStorage.setItem("aa_trusted", JSON.stringify(map));
      toast.success(t("trusted_forgotten"));
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!data) return null;
  const n = data.length;
  return (
    <Row icon={ShieldCheck} title={t("trusted_devices")} text={`${t("trusted_text")} · ${n} ${t("trusted_active")}`} testid="trusted-devices-row">
      {n > 0 && <button onClick={forget} className="rounded-full border-2 border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100" data-testid="trusted-devices-forget">{t("trusted_forget")}</button>}
    </Row>
  );
};

export const AppPanel = () => {
  const { t } = useLang();
  const [, tick] = useState(0);
  const [notif, setNotif] = useState(notifyEnabled());
  useEffect(() => { const off = onPwaChange(() => tick((x) => x + 1)); return () => { off(); }; }, []);
  const install = async () => { if (await promptInstall()) toast.success(t("app_installed")); };
  const toggle = async () => {
    try {
      if (notif) { setNotif(false); await disableNotifications(); return; }
      const r = await enableNotifications();
      if (r !== "granted") return toast.error(r === "unsupported" ? t("notif_unsupported") : t("notif_denied"));
      setNotif(true);
      toast.success(t("notif_test"));
    } catch (e) {
      toast.error(t("notif_denied"));
    }
  };
  const installText = isStandalone() ? t("app_installed") : canInstall() ? t("install_text") : isIos() ? t("install_ios") : t("install_browser");
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8" data-testid="app-panel">
      <h2 className="mb-6 font-display text-xl font-bold text-[#0A2540]">{t("app_notifications")}</h2>
      <div className="space-y-3">
        <Row icon={Smartphone} title={t("install_app")} text={installText} testid="app-install-row">
          {isStandalone() ? <CheckCircle2 className="text-emerald-500" data-testid="app-installed-badge" />
            : canInstall() && <button onClick={install} className="btn-buoy !py-2" data-testid="app-install-button"><Download size={16} />{t("install_app")}</button>}
        </Row>
        <Row icon={notif ? Bell : BellOff} title={t("notifications")} text={notifySupported() ? t("notif_text") : t("notif_unsupported")} testid="app-notif-row">
          {notifySupported() && <button onClick={toggle} className={notif ? "rounded-full border-2 border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100" : "btn-buoy !py-2"} data-testid="app-notif-toggle">
            {notif ? t("notif_disable") : t("notif_enable")}</button>}
        </Row>
        <TrustedRow />
      </div>
    </section>
  );
};
